/**
 * Helper condiviso per creare le Relationship polimorfiche inferite dalle
 * `MessageInfo` di un messaggio, quando un preventivo nasce o viene collegato a
 * partire da un Message.
 *
 * Per ogni info con un riferimento a entità valido (`hasEntityRef`) crea una
 * relazione bidirezionale fra quella entità — Person, Business, … — e il
 * preventivo, con la stessa descrizione nei due versi.
 *
 * Best-effort in parallelo: `Promise.allSettled` lancia tutte le dispatch
 * insieme, ed è più veloce della sequenza senza che il fallimento di una
 * interrompa le altre. Le fallite finiscono in `console.warn` con il dettaglio
 * del reject — serve a diagnosticare 409 da duplicato o errori di rete senza
 * bloccare la navigazione dell'utente dopo il salvataggio.
 */

import { createBidirectionalRelationship } from '../relationship/relationshipThunks';
import { hasEntityRef, type MessageInfo } from '../message/messageInfoTypes';
import type { AppDispatch } from '../../store/store';

export async function linkInfosToQuotation(
  dispatch: AppDispatch,
  infos: MessageInfo[],
  quotationId: number,
  quotationFqn: string,
  description: string,
): Promise<void> {
  const targets = infos.filter(hasEntityRef);
  if (targets.length === 0) return;
  const results = await Promise.allSettled(
    targets.map((info) =>
      dispatch(createBidirectionalRelationship({
        className1: info.className,
        classPK1: info.classPK,
        className2: quotationFqn,
        classPK2: quotationId,
        descriptionForward: description,
        descriptionReverse: description,
      })).unwrap(),
    ),
  );
  const failed = results
    .map((r, idx) => ({ r, info: targets[idx] }))
    .filter((entry) => entry.r.status === 'rejected');
  if (failed.length > 0) {
    console.warn(
      `[linkInfosToQuotation] ${failed.length}/${targets.length} relationships failed`,
      failed.map((entry) => ({
        className: entry.info.className,
        classPK: entry.info.classPK,
        reason: entry.r.status === 'rejected' ? entry.r.reason : undefined,
      })),
    );
  }
}
